import { ChangeDetectorRef, inject, Injectable, NgZone, OnDestroy } from '@angular/core';

import {
  BehaviorSubject,
  shareReplay,
  catchError,
  Observable,
  takeUntil,
  finalize,
  Subject,
  first,
  scan,
  tap,
  of,
  throwError,
} from 'rxjs';

@Injectable()
export class LoadHelper implements OnDestroy {
  readonly progressbarSource: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly progressbar$ = this.progressbarSource.asObservable().pipe(shareReplay(1));

  private readonly pendingCounterSource$: Subject<boolean> = new Subject<boolean>();
  private readonly pendingCounter$ = this.pendingCounterSource$.asObservable();

  private readonly _destroyed$ = new Subject<void>();

  private readonly _cdr = inject(ChangeDetectorRef);

  private readonly _ngZone = inject(NgZone);

  constructor() {
    this.subscribeCounter();
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  loading<T>(source: Observable<T>, replay: boolean = false, caughtError = true): Observable<T> {
    const loadingHandler = source.pipe(
      tap({
        subscribe: () => {
          this.counter(true);
        },
      }),
      finalize(() => {
        this.counter(false);
      }),
      catchError(error => {
        return caughtError ? of(error as any) : throwError(() => error);
      }),
      first()
    );
    return replay ? loadingHandler.pipe(shareReplay(1)) : loadingHandler;
  }

  counter(status: boolean) {
    this._ngZone.run(() => {
      this.pendingCounterSource$.next(status);
      this._cdr.detectChanges();
    });
  }

  private subscribeCounter() {
    this.pendingCounter$
      .pipe(
        scan((acc, curr) => (curr ? acc + 1 : acc > 0 ? acc - 1 : acc), 0),
        takeUntil(this._destroyed$)
      )
      .subscribe(count => {
        if (count) {
          this.progressbarSource.next(true);
        } else {
          this.progressbarSource.next(false);
        }
      });
  }
}
